/**
 * Seed data loader - handles both inline arrays and SeedConfig objects
 */

import type { SeedConfig } from '@gzl10/nexus-sdk'
import type { Logger } from 'pino'

type SeedInput = Array<Record<string, unknown>> | SeedConfig | undefined

/**
 * Load seed data from various sources (inline array, URL, file, or API)
 *
 * @param seed - Inline array or SeedConfig object
 * @param logger - Logger instance for error reporting
 * @param entityType - Entity type name for logging context
 * @returns Array of seed data items, or empty array if no data
 */
export async function loadSeedData(
  seed: SeedInput,
  logger: Logger,
  entityType: string
): Promise<Array<Record<string, unknown>>> {
  if (!seed) {
    return []
  }

  // If it's already an array, return it directly
  if (Array.isArray(seed)) {
    return seed
  }

  // Otherwise it's a SeedConfig object
  const config = seed as SeedConfig

  switch (config.source) {
    case 'inline':
      return config.data ?? []

    case 'url':
    case 'api':
      if (!config.url) {
        logger.warn({ entityType }, 'Seed config has source=url but no URL provided')
        return []
      }
      try {
        const response = await fetch(config.url, {
          headers: config.headers
        })
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        const data = await response.json() as Record<string, unknown> | Record<string, unknown>[]
        // If transform function is provided as string, it's not supported in runtime
        // Just return the data as-is
        return Array.isArray(data) ? data : [data]
      } catch (error) {
        logger.error({ entityType, url: config.url, error }, 'Failed to load seed data from URL')
        return []
      }

    case 'file':
      // File loading would require fs access, not commonly needed
      logger.warn({ entityType }, 'Seed source=file is not supported at runtime')
      return []

    default:
      logger.warn({ entityType, source: config.source }, 'Unknown seed source type')
      return []
  }
}
